import { getRenderFamily } from './applicationQuestionTypes';
import { resolveTypeConfig } from './applicationQuestionTypeConfig';
import {
  coerceDataGridAnswerValue,
  dataGridColumns,
  isDataGridQuestionType,
} from './dataGridAnswerDisplay';
import {
  normalizeMultipleChoiceOtherValue,
  normalizeSingleChoiceOtherValue,
} from './applicationQuestionValidation';

const UPLOAD_QUESTION_TYPES = new Set([
  'FILE',
  'IMAGE',
  'VIDEO',
  'AUDIO',
  'FILE_MULTI',
  'IMAGE_GALLERY',
  'SIGNATURE_UPLOAD',
  'CAMERA_CAPTURE',
]);

export function isUploadQuestionType(question) {
  const type = String(question?.type || '').toUpperCase();
  if (UPLOAD_QUESTION_TYPES.has(type)) return true;
  const family = getRenderFamily(type);
  return ['file', 'image', 'video', 'audio', 'camera', 'signature-upload'].includes(family);
}

export function letteredRowOptionCount(question) {
  const type = String(question?.type || '').toUpperCase();
  const opts = Array.isArray(question?.options) ? question.options : [];
  if (type === 'LABELED_LIST') return opts.length || 3;
  if (type === 'TABLE_ROWS') return opts.length || 2;
  return 0;
}

export function normalizeLetteredRowAnswer(value, question) {
  const type = String(question?.type || '').toUpperCase();
  if (type !== 'LABELED_LIST' && type !== 'TABLE_ROWS') return value;
  const count = letteredRowOptionCount(question);
  if (!count) return Array.isArray(value) ? value : [];
  const src = Array.isArray(value) ? value : [];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push(src[i] != null ? String(src[i]) : '');
  }
  return out;
}

export function formatLetteredRowAnswerText(value, question) {
  const type = String(question?.type || '').toUpperCase();
  if (type !== 'LABELED_LIST' && type !== 'TABLE_ROWS') return '';
  const opts = Array.isArray(question?.options) ? question.options : [];
  const rows = normalizeLetteredRowAnswer(value, question);
  const lines = rows.map((row, i) => {
    const letter = String.fromCharCode(97 + i);
    const cell = String(row ?? '').trim() || '—';
    if (type === 'TABLE_ROWS') {
      const desc = opts[i] != null ? String(opts[i]).trim() : '';
      return desc ? `${letter}) ${desc}: ${cell}` : `${letter}) ${cell}`;
    }
    return `${letter}. ${cell}`;
  });
  return lines.join('\n');
}

function matrixRows(question) {
  const cfg = resolveTypeConfig(question);
  if (Array.isArray(cfg.rows) && cfg.rows.length) return cfg.rows.map(String);
  const opts = Array.isArray(question?.options) ? question.options : [];
  const labels = opts.map((o) => (typeof o === 'object' ? (o.label ?? o.value ?? '') : String(o ?? '')));
  return labels.filter(Boolean).length ? labels : ['Row 1', 'Row 2', 'Row 3'];
}

export function coerceMatrixAnswerValue(value, question) {
  const rows = matrixRows(question);
  const type = String(question?.type || '').toUpperCase();
  const isMulti = type === 'MATRIX_MULTI' || getRenderFamily(type) === 'matrix-multi';
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const out = {};
  rows.forEach((_, i) => {
    const key = String(i);
    const cell = raw[key] ?? raw[i];
    if (isMulti) {
      out[key] = Array.isArray(cell) ? cell.map(String) : (cell != null && cell !== '' ? [String(cell)] : []);
    } else {
      out[key] = cell != null ? String(cell) : '';
    }
  });
  return out;
}

export function coerceSideBySideAnswerValue(value, question) {
  const cfg = resolveTypeConfig(question);
  const columns = Array.isArray(cfg.columns) ? cfg.columns : [];
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const out = {};
  columns.forEach((c, i) => {
    const key = typeof c === 'object' ? String(c.key || `col${i + 1}`) : `col${i + 1}`;
    out[key] = raw[key] != null ? String(raw[key]) : '';
  });
  return out;
}

export function coerceRepeatingAnswerValue(value, question) {
  if (isDataGridQuestionType(question)) return coerceDataGridAnswerValue(value, question);
  const cols = dataGridColumns(question, value);
  let rows = [];
  if (Array.isArray(value)) {
    rows = value;
  } else if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
      rows = keys.sort((a, b) => Number(a) - Number(b)).map((k) => value[k]);
    } else {
      rows = [value];
    }
  }
  return rows.map((row) => {
    const src = row && typeof row === 'object' && !Array.isArray(row) ? row : {};
    return Object.fromEntries(cols.map((c) => [c.key, src[c.key] != null ? String(src[c.key]) : '']));
  });
}

export function coerceAnswerValueForQuestion(question, value) {
  if (!question) return value;
  const type = String(question?.type || '').toUpperCase();
  if (isDataGridQuestionType(question)) return coerceDataGridAnswerValue(value, question);
  if (type === 'REPEATING_SECTION' || getRenderFamily(type) === 'repeating') {
    return coerceRepeatingAnswerValue(value, question);
  }
  if (type === 'MATRIX_SINGLE' || type === 'MATRIX_MULTI') return coerceMatrixAnswerValue(value, question);
  if (type === 'SIDE_BY_SIDE_CHOICE' || getRenderFamily(type) === 'side-by-side-choice') {
    return coerceSideBySideAnswerValue(value, question);
  }
  if (type === 'LABELED_LIST' || type === 'TABLE_ROWS') {
    return normalizeLetteredRowAnswer(value, question);
  }
  if (['SINGLE_CHOICE_OTHER', 'DROPDOWN_OTHER'].includes(type)) return normalizeSingleChoiceOtherValue(value);
  if (type === 'MULTIPLE_CHOICE_OTHER') return normalizeMultipleChoiceOtherValue(value);
  return value;
}

export function isComplexAnswerEmpty(question, value) {
  if (!question) return value == null || value === '';
  const type = String(question?.type || '').toUpperCase();
  if (isDataGridQuestionType(question)) {
    const rows = coerceDataGridAnswerValue(value, question);
    return !rows.some((row) => Object.values(row || {}).some((c) => String(c ?? '').trim()));
  }
  if (type === 'REPEATING_SECTION' || getRenderFamily(type) === 'repeating') {
    const rows = coerceRepeatingAnswerValue(value, question);
    return !rows.some((row) => Object.values(row || {}).some((c) => String(c ?? '').trim()));
  }
  if (type === 'MATRIX_SINGLE' || type === 'MATRIX_MULTI') {
    const rows = matrixRows(question);
    const val = coerceMatrixAnswerValue(value, question);
    const isMulti = type === 'MATRIX_MULTI';
    return rows.some((_, i) => {
      const cell = val[String(i)];
      if (isMulti) return !Array.isArray(cell) || !cell.length;
      return !String(cell ?? '').trim();
    });
  }
  if (type === 'SIDE_BY_SIDE_CHOICE') {
    const val = coerceSideBySideAnswerValue(value, question);
    return !Object.values(val).some((c) => String(c ?? '').trim());
  }
  if (type === 'LABELED_LIST' || type === 'TABLE_ROWS') {
    const rows = normalizeLetteredRowAnswer(value, question);
    return !rows.some((r) => String(r ?? '').trim());
  }
  return null;
}
